// components/private/order/RegularCardView.tsx
import { useEffect, useState } from "react";
import {
    Download, CheckCircle2, Mail, PhoneCall,
    Loader2, Send, CreditCard, Hash,
    Image as ImageIcon, Layers, // ✅ Layers sudah ditambahkan
    PrinterCheck
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { axiosClient } from "@/lib/axiosClient";
import toast from "react-hot-toast";

interface UserProps {
    participant_id: number;
    name?: string;
    email?: string;
}

interface CardData {
    registration_id: number;
    participant_name: string;
    package_name: string;
    registration_number: string;
    qr_code_url: string | null;
    status_pembayaran: string;
    expired_at: string;
    invoice_number: string;
}

interface RegularCardViewProps {
    user: UserProps;
    activePeriodId: number;
}

export const RegularCardView = ({ user, activePeriodId }: RegularCardViewProps) => {
    const [cardData, setCardData] = useState<CardData | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    // State untuk action buttons
    const [isDownloading, setIsDownloading] = useState<boolean>(false);
    const [isSendingEmail, setIsSendingEmail] = useState<boolean>(false);

    useEffect(() => {
        const fetchAllData = async () => {
            if (!user.participant_id || !activePeriodId) return;
            try {
                setLoading(true);
                // 1. Fetch JSON Data
                const { data } = await axiosClient.get<{ data: CardData }>(
                    `/participants/${user.participant_id}/registrations/period/${activePeriodId}/card`
                );
                setCardData(data.data);

                // 2. Fetch Gambar Jpeg sebagai Blob untuk ditampilkan sebagai Preview
                try {
                    const response = await axiosClient.get(
                        `/participants/${user.participant_id}/registrations/period/${activePeriodId}/card-image`,
                        { responseType: 'blob' }
                    );
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    setPreviewUrl(url);
                } catch (imgError) {
                    console.error("Gagal memuat preview gambar:", imgError);
                }

            } catch (error) {
                console.error("Gagal memuat kartu:", error);
                toast.error("Gagal memuat data kartu peserta.");
            } finally {
                setLoading(false);
            }
        };
        fetchAllData();
    }, [user.participant_id, activePeriodId]);

    // Bersihkan memory browser dari Object URL saat komponen dilepas
    useEffect(() => {
        return () => {
            if (previewUrl) window.URL.revokeObjectURL(previewUrl);
        };
    }, [previewUrl]);

    // Fungsi Download Gambar Kartu (JPEG)
    const handleDownloadImage = async () => {
        setIsDownloading(true);
        try {
            if (previewUrl) {
                const link = document.createElement('a');
                link.href = previewUrl;
                link.setAttribute('download', `${cardData?.registration_number}_ID_Card.jpeg`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                toast.success("Kartu peserta berhasil diunduh.");
            } else {
                const response = await axiosClient.get(
                    `/participants/${user.participant_id}/registrations/period/${activePeriodId}/card-image`,
                    { responseType: 'blob' }
                );
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `${cardData?.registration_number}_ID_Card.jpeg`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                toast.success("Kartu peserta berhasil diunduh.");
            }
        } catch (error) {
            console.error("Download error:", error);
            toast.error("Gagal mengunduh gambar kartu.");
        } finally {
            setIsDownloading(false);
        }
    };

    // Fungsi Kirim Ulang Email
    const handleSendEmail = async () => {
        setIsSendingEmail(true);
        try {
            const { data } = await axiosClient.post(
                `/participants/${user.participant_id}/registrations/period/${activePeriodId}/send-card`
            );
            toast.success(data.message || "Email Kartu Peserta berhasil dikirim.");
        } catch (error) {
            console.error("Email error:", error);
            toast.error("Gagal mengirim email kartu peserta.");
        } finally {
            setIsSendingEmail(false);
        }
    };

    // === REFINED SKELETON UI (Dynamic Theme) ===
    if (loading) {
        return (
            <div className="mx-auto space-y-6 animate-pulse">
                {/* Skeleton Kartu */}
                <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-[2.5rem] p-6 md:p-10 flex flex-col md:flex-row gap-10">
                    <div className="w-full md:w-[360px] aspect-[54/86] bg-slate-100 dark:bg-slate-800 rounded-[1.8rem]" />
                    <div className="flex-1 space-y-6">
                        <div className="h-8 bg-slate-100 dark:bg-slate-800 rounded-lg w-3/4" />
                        <div className="h-20 bg-slate-100 dark:bg-slate-800 rounded-2xl w-full" />
                        <div className="flex gap-3">
                            <div className="h-12 flex-1 bg-slate-100 dark:bg-slate-800 rounded-xl" />
                            <div className="h-12 flex-1 bg-slate-100 dark:bg-slate-800 rounded-xl" />
                        </div>
                    </div>
                </div>
                {/* Skeleton Footer */}
                <div className="h-28 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-[2rem]" />
            </div>
        );
    }

    if (!cardData) return null;

    return (
        // ✅ Layout diubah jadi mx-auto agar terpusat
        <div className="mx-auto space-y-6 animate-in fade-in duration-500">

            <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-[2.5rem] shadow-sm p-6 md:p-10">
                <div className="flex flex-col md:flex-row gap-10">

                    {/* SISI KIRI: Pratinjau Gambar Asli */}
                    <div className="w-full md:w-[360px] shrink-0 mx-auto md:mx-0">
                        <div className="p-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-[1.8rem] shadow-xl">
                            {previewUrl ? (
                                <img
                                    src={previewUrl}
                                    alt="ID Card Preview"
                                    className="w-full h-auto object-contain rounded-2xl border border-slate-200/50 dark:border-slate-700/50 pointer-events-none"
                                />
                            ) : (
                                <div className="w-full aspect-[54/86] bg-slate-100 dark:bg-slate-900 rounded-2xl flex flex-col items-center justify-center text-slate-400 gap-3 border border-slate-200 dark:border-slate-700">
                                    <ImageIcon className="w-8 h-8 opacity-50" />
                                    <span className="text-xs font-medium">Memuat Pratinjau...</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* SISI KANAN: Informasi & Tombol Aksi */}
                    <div className="flex-1 flex flex-col justify-center space-y-8">
                        <div>
                            <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 rounded-full text-[10px] font-black uppercase tracking-widest mb-4">
                                <CheckCircle2 className="w-3.5 h-3.5" /> Akses Diberikan
                            </div>
                            <h3 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight mb-2">
                                Kartu Identitas Anda Telah Siap.
                            </h3>
                            <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed font-medium">
                                Kartu ini adalah akses resmi Anda untuk mengikuti seluruh rangkaian kegiatan Konvensi Nasional Pendidikan Indonesia (KONASPI) XII.
                            </p>
                        </div>

                        {/* Detail Pendaftaran */}
                        <div className="space-y-4 p-5 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-700">
                            <div>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1.5 mb-1">
                                    <Layers className="w-3 h-3" /> Paket Kegiatan
                                </p>
                                <p className="text-sm font-bold text-slate-800 dark:text-slate-200 truncate" title={cardData.package_name}>
                                    {cardData.package_name}
                                </p>
                            </div>
                            <div className="grid grid-cols-2 gap-4 pt-3 border-t border-slate-200 dark:border-slate-700">
                                <div>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1.5 mb-1">
                                        <Hash className="w-3 h-3" /> No. Registrasi
                                    </p>
                                    <p className="text-sm font-mono font-bold text-slate-800 dark:text-slate-200">{cardData.registration_number}</p>
                                </div>
                                <div>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1.5 mb-1">
                                        <CreditCard className="w-3 h-3" /> No. Invoice
                                    </p>
                                    <p className="text-sm font-mono font-bold text-slate-800 dark:text-slate-200">{cardData.invoice_number}</p>
                                </div>
                            </div>
                        </div>

                        {/* Info Cetak Kartu: peserta tidak perlu cetak mandiri, panitia yang akan memproses */}
                        <div className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-blue-600 to-indigo-700 rounded-2xl p-5 shadow-lg shadow-blue-600/20">
                            <div className="absolute -top-6 -right-6 w-28 h-28 bg-white/10 rounded-full blur-2xl pointer-events-none" />
                            <div className="relative flex gap-3.5">
                                <div className="shrink-0 w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center">
                                    <PrinterCheck className="w-4 h-4 text-white" />
                                </div>
                                <div className="space-y-1.5">
                                    <p className="text-sm font-black text-white flex items-center gap-1.5">
                                        Informasi Cetak Kartu
                                    </p>
                                    <p className="text-xs text-blue-50/90 leading-relaxed font-medium">
                                        Anda tidak perlu mencetak kartu ini secara mandiri. Panitia KONASPI XII akan memproses pencetakan kartu fisik dan menyediakannya saat Anda check-in di lokasi acara. Jika ingin menyimpan salinan digital sebagai cadangan, tekan tombol <span className="font-bold text-white">"Kirim ke Email"</span> di bawah untuk menerima kartu virtual langsung ke inbox Anda.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Grup Tombol Aksi */}
                        <div className="flex flex-col sm:flex-row gap-3">
                            <Button
                                onClick={handleDownloadImage}
                                disabled={isDownloading || !previewUrl}
                                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold h-12 rounded-xl shadow-lg shadow-blue-500/30 transition-all active:scale-95"
                            >
                                {isDownloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                                Unduh Kartu (JPEG)
                            </Button>

                            <Button
                                onClick={handleSendEmail}
                                disabled={isSendingEmail}
                                variant="outline"
                                className="flex-1 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 font-bold h-12 rounded-xl transition-all active:scale-95 text-slate-700 dark:text-slate-300"
                            >
                                {isSendingEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                                Kirim ke Email
                            </Button>
                        </div>
                    </div>
                </div>

            </div>

            {/* Footer Bantuan */}
            <div className="flex flex-col md:flex-row justify-between items-center p-8 bg-white dark:bg-slate-900 rounded-[2rem] border border-slate-100 dark:border-slate-800 shadow-sm gap-6">
                <div className="space-y-1 text-center md:text-left">
                    <h4 className="text-sm font-black text-slate-900 dark:text-white flex items-center gap-2 justify-center md:justify-start uppercase tracking-tight leading-none">
                        <PhoneCall className="w-4 h-4 text-blue-600" /> Pusat Bantuan
                    </h4>
                    <p className="text-[11px] text-slate-400 font-medium italic mt-2">Butuh bantuan teknis terkait kartu Anda?</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-6">
                    <a href={`mailto:konaspixii@undiksha.ac.id?subject=Bantuan Registrasi ${cardData.registration_number}`} className="flex items-center gap-2 text-xs font-bold text-blue-600 hover:underline tracking-tight">
                        <Mail className="w-4 h-4" /> konaspixii@undiksha.ac.id
                    </a>
                </div>
            </div>
        </div>
    );
};